import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  addHours,
  addMinutes,
  isSameDay,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import {
  apiForLine,
  isLineReady,
  lineNotReadyReason,
  LAM_LINE_HEADING,
  type LamLineSelection,
  type LamRollEvent,
  type LamShiftRollsRow,
} from "../../../api/lamLine";
import CardHeaderRow from "../../common/CardHeaderRow";
import LineSelector from "../../common/LineSelector";
import UnavailablePanel from "../../common/UnavailablePanel";
import {
  nashvilleLocalToISO,
  nowAsNashvilleLocal,
} from "../../../utils/nashvilleTime";
import {
  getShiftSchedule,
  shiftStartMinutes,
  type ShiftDefinition,
} from "../../../api/shifts";
import Lam2RollTimeline from "./Lam2RollTimeline";
import { ROLL_COLORS, RollsLegend, fmtRolls } from "./Lam2RollsChart";
import ShiftBrowser, { shiftLabel, type ShiftSlot } from "./ShiftBrowser";

/**
 * Roll quality on a clock, one shift at a time.
 *
 * Self-contained (fetches its own shifts and roll events) so the Rolls tab and
 * the Operations Overview show exactly the same thing rather than two
 * reimplementations that drift apart.
 *
 * A shift is the unit because it is the unit the PLC counts in and the unit the
 * plant is staffed in: "what did the night crew on the 3rd produce" is a
 * question about a shift, and a window of "the last four hours" straddling two
 * of them cannot answer it. The card opens on the shift the schedule says is
 * running now — whether or not this line has counted anything in it, so the
 * Lam 1 and Lam 2 cards always open on the same shift — and the browser walks
 * back from there.
 */

/**
 * A/B/F counts for the shift on screen, in the legend's place: each count sits
 * on its grade's color, so it is the key to the strip as well as the tally, and
 * a separate legend would only repeat the swatches. The running shift's counts
 * climb as it refreshes. A shift that has not started yet has no counts, so it shows
 * dashes rather than zeros that would read as a shift that produced nothing.
 */
const ShiftTotals: React.FC<{ row?: LamShiftRollsRow; loading?: boolean }> = ({
  row,
  loading = false,
}) => {
  const grades: Array<[string, number | undefined, string]> = [
    ["A", row?.a_rolls, ROLL_COLORS.a],
    ["B", row?.b_rolls, ROLL_COLORS.b],
    ["F", row?.f_rolls, ROLL_COLORS.f],
  ];
  const show = (n: number | undefined) => (row ? fmtRolls(n ?? 0) : "\u2014");
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      data-testid="shift-totals"
      sx={{ whiteSpace: "nowrap" }}
    >
      {grades.map(([k, n, c]) => (
        <Stack key={k} direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: c }}
          />
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {k}{" "}
            {/* A dash while loading would read as "this shift has no count". */}
            {loading ? (
              <Skeleton variant="text" width={28} sx={{ display: "inline-block" }} />
            ) : (
              show(n)
            )}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const REFRESH_MS = 60_000;
/** How many shifts back the browser can reach. The API caps this at 100. */
const SHIFT_LIMIT = 100;
/** No shift runs longer than this, so a roll past it belongs to the next one. */
const MAX_SHIFT_HOURS = 12;

/** Same wall-clock start, to the minute. */
const sameStart = (a: Date, b: Date) =>
  isSameDay(a, b) &&
  a.getHours() === b.getHours() &&
  a.getMinutes() === b.getMinutes();

type Props = {
  title?: string;
  /** The overview leads with h5 cards; the tab sits under a page heading. */
  titleVariant?: "h5" | "h6";
  subtitle?: string;
  /** The line to open on, and to stay on when there is no switch. */
  line?: LamLineSelection;
  /**
   * Show a line switch this card owns.
   *
   * Owns, rather than reports upward: which line you are watching roll by roll
   * is a different question from which line you are totting up over the last 30
   * days, and wanting one does not mean wanting the other. A single-line page
   * leaves this off and the line stays pinned.
   */
  selectableLine?: boolean;
};

const QualityTimelineCard: React.FC<Props> = ({
  title = "Quality Timeline",
  titleVariant = "h6",
  subtitle,
  line: pinnedLine = "lam2",
  selectableLine = false,
}) => {
  const [chosenLine, setChosenLine] = useState<LamLineSelection>(pinnedLine);
  const line = selectableLine ? chosenLine : pinnedLine;

  const [shifts, setShifts] = useState<LamShiftRollsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<LamRollEvent[]>([]);
  /**
   * Which shift window `events` belongs to. The markers arrive in a second
   * request after the shifts, so without this the spinner cleared first and the
   * rolls popped onto an empty strip a beat later. Keyed on the window rather
   * than a boolean so the 60s refresh — which refetches the same window — keeps
   * the markers on screen instead of flashing the spinner every minute.
   */
  const [eventsFor, setEventsFor] = useState<string | null>(null);
  /**
   * Which shift is on screen, as an index into `shifts`. Null means "whichever
   * is newest" — the state the card opens in, and the one it stays in as new
   * shifts arrive, so a dashboard left up keeps following the line. Picking a
   * shift pins it, and the refresh below stops moving the view.
   */
  const [pinned, setPinned] = useState<number | null>(null);
  /** What shifts the plant defines, so today's can be offered before they run. */
  const [schedule, setSchedule] = useState<ShiftDefinition[]>([]);

  const unavailable = !isLineReady(line);

  useEffect(() => {
    const ctrl = new AbortController();
    getShiftSchedule(ctrl.signal)
      .then(setSchedule)
      .catch(() => {
        /* without it, only shifts the PLC has already recorded are offered */
      });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    // Keep the last real strip to blur as the template, and skip a fetch whose
    // result we would not draw.
    if (!isLineReady(line)) {
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    // Drop the previous line's shifts and rolls before fetching: leaving them
    // on screen under the new heading would read as this line's output.
    setShifts([]);
    setEvents([]);
    setEventsFor(null);
    setPinned(null);
    setLoading(true);

    const load = async (silent = false) => {
      try {
        const data = await apiForLine(line).getShiftRolls({
          limit: SHIFT_LIMIT,
          signal: ctrl.signal,
        });
        setShifts(data);
      } catch {
        /* leave the last good data on screen */
      } finally {
        if (!silent) setLoading(false);
      }
    };

    load();
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => {
      clearInterval(id);
      ctrl.abort();
    };
  }, [line]);

  /**
   * Every shift you can browse to: the ones the PLC recorded, plus any of
   * today's that it has not, plus the one running now if it began yesterday.
   *
   * The roll tables only gain a row once a shift starts counting, so tonight's
   * shift would otherwise be invisible until it began — you could not look at
   * what is coming, only at what had already happened. Today's scheduled shifts
   * are added as empty slots so the whole day is on screen from midnight.
   *
   * Only today's. A day the line did not run has no slots and stays greyed out
   * in the calendar; sprouting empty buttons across every past day would turn
   * "nothing ran" into "nothing recorded", which are not the same thing.
   *
   * `days` on the definition is deliberately not consulted. Which weekdays this
   * line actually counts lives in the PLC, not in settings_shifts, so filtering
   * on it risks hiding a shift that is about to run.
   *
   * `running` is the start of the shift the schedule says is on now: the latest
   * scheduled start, today or yesterday, that has passed. After midnight that is
   * last night's shift, which today's slots would not include.
   */
  const { slots, running } = useMemo(() => {
    const recorded: ShiftSlot[] = shifts
      .map((row) => ({
        start: parseISO(row.shift_start),
        label: shiftLabel(row.shift_name),
        row,
      }))
      .filter((slot) => !Number.isNaN(slot.start.getTime()));

    const now = nowAsNashvilleLocal();
    const today = startOfDay(now);
    const starts = (day: Date) =>
      schedule.flatMap((def) => {
        const min = shiftStartMinutes(def.start);
        return min === null
          ? []
          : [{ start: addMinutes(day, min), label: shiftLabel(def.name) }];
      });

    const todays = starts(today);
    const running = [...starts(subDays(today, 1)), ...todays]
      .filter((c) => c.start <= now)
      .sort((a, b) => b.start.getTime() - a.start.getTime())[0];

    const wanted =
      running && !todays.some((c) => sameStart(c.start, running.start))
        ? [running, ...todays]
        : todays;
    const scheduled: ShiftSlot[] = wanted.filter(
      (c) => !recorded.some((slot) => sameStart(slot.start, c.start)),
    );

    return {
      slots: [...recorded, ...scheduled].sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      ),
      running: running?.start ?? null,
    };
  }, [shifts, schedule]);

  /**
   * Open on the shift running now by the schedule, recorded or not, so both
   * lines' cards land on the same shift. Without a schedule to go by, fall back
   * to the newest shift that has actually run.
   */
  const defaultIndex = useMemo(() => {
    if (running) {
      const i = slots.findIndex((slot) => sameStart(slot.start, running));
      if (i >= 0) return i;
    }
    for (let i = slots.length - 1; i >= 0; i--) if (slots[i].row) return i;
    return slots.length - 1;
  }, [slots, running]);

  const index = pinned ?? defaultIndex;
  const current = slots[index];

  /**
   * The clock bounds of the shift on screen: its whole slot, start to end.
   *
   * The end is the next shift's start, or a full shift's length when there is
   * no next shift yet — which also caps it, so a stretch of downtime cannot
   * stretch one shift across the days until the line next ran.
   *
   * Deliberately the slot and not "now". The running shift keeps its full
   * width and fills up as it goes: the axis lands on the same two-hourly marks
   * all shift, a 06:00 shift always reads 06:00 to 18:00, and two shifts can be
   * compared without one of them being a different length because of the time
   * of day the page happened to be open. The empty stretch to the right of the
   * last roll is the part of the shift still to run.
   *
   * The timeline needs this said out loud. Left to infer the span from a single
   * shift, it has no gap to measure and falls back to a nominal eight hours,
   * which ends a 06:00 shift at 14:00 no matter what time it actually is.
   */
  const bounds = useMemo(() => {
    if (!current) return null;
    const from = current.start;

    const nominalEnd = addHours(from, MAX_SHIFT_HOURS).getTime();
    const next = slots[index + 1];
    const to = next ? Math.min(next.start.getTime(), nominalEnd) : nominalEnd;
    return to > from.getTime() ? { from: from.getTime(), to } : null;
  }, [current, slots, index]);

  useEffect(() => {
    if (!isLineReady(line) || !bounds) return;

    const ctrl = new AbortController();
    const key = `${bounds.from}-${bounds.to}`;
    apiForLine(line)
      .getRollEvents({
        start: nashvilleLocalToISO(new Date(bounds.from)),
        end: nashvilleLocalToISO(new Date(bounds.to)),
        signal: ctrl.signal,
      })
      .then((ev) => {
        setEvents(ev);
        setEventsFor(key);
      })
      .catch(() => {
        /* older shifts simply have no timing; keep the band fallback */
        if (!ctrl.signal.aborted) setEventsFor(key);
      });
    return () => ctrl.abort();
  }, [line, bounds]);

  // The timeline draws one band per row; browsing means exactly one — or none,
  // for a shift that has not run, which draws its empty slot.
  // Until this shift's markers land, the strip is still loading. A shift that
  // cannot have any (no bounds) has nothing to wait for.
  const eventsLoading =
    bounds != null && eventsFor !== `${bounds.from}-${bounds.to}`;

  const timelineRows = useMemo(
    () => (current?.row ? [current.row] : []),
    [current],
  );

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 1.25, sm: 2 } }}>
        <CardHeaderRow
          mb={1.5}
          title={
            <div>
              <Typography
                variant={titleVariant}
                sx={{ fontWeight: titleVariant === "h5" ? 700 : undefined }}
              >
                {/* With a line switch on the card, the heading has to say
                    which line is on screen — otherwise the two look
                    identical. */}
                {selectableLine
                  ? `${LAM_LINE_HEADING[line]} — ${title}`
                  : title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </div>
          }
          controls={
            <>
              {selectableLine && (
                <LineSelector value={line} onChange={setChosenLine} />
              )}
              <ShiftBrowser
                slots={slots}
                index={slots.length ? index : -1}
                onIndexChange={(i) =>
                  setPinned(Math.min(Math.max(i, 0), slots.length - 1))
                }
              />
            </>
          }
          // A blurred line has no real counts, so it keeps the plain legend.
          aside={
            unavailable ? (
              <RollsLegend />
            ) : (
              <ShiftTotals row={current?.row} loading={loading} />
            )
          }
        />

        {unavailable ? (
          <UnavailablePanel reason={lineNotReadyReason(line)}>
            <Lam2RollTimeline
              rows={timelineRows}
              events={events}
              window={bounds}
            />
          </UnavailablePanel>
        ) : (
          <LoadingOverlay show={loading || eventsLoading} minHeight={80}>
            {slots.length === 0 && !loading ? (
              <Typography variant="body2" color="text.secondary">
                No shifts recorded yet for this line.
              </Typography>
            ) : (
              <Lam2RollTimeline
                rows={timelineRows}
                events={events}
                window={bounds}
              />
            )}
          </LoadingOverlay>
        )}
      </CardContent>
    </Card>
  );
};

export default QualityTimelineCard;
